import type { JsonValue } from "@e308/core";
import {
  REPEATABLES,
  type RepeatableId,
  SPECIALIZATIONS,
  TECHNOLOGIES,
} from "./content/research.js";
import { LIMITS } from "./content/setup.js";
import {
  ceilRational,
  formatMicro,
  microFromString,
  powerRational,
  rationalFromDecimal,
} from "./exact-numbers.js";
import { exactRecordKeys as exactKeys, recordObject } from "./record-validation.js";
import type { ResearchOrder, ResearchState } from "./research-types.js";

const INITIAL: ResearchState = {
  nextOrderId: 1,
  queue: [],
  active: null,
  completed: [],
  industrialSpecialization: null,
  militarySpecialization: null,
  repeatableLevels: {
    "mining-output": 0,
    "construction-output": 0,
    "assembly-output": 0,
    "shield-output": 0,
  },
};

export function initialResearchState(): ResearchState & JsonValue {
  return clone(INITIAL) as ResearchState & JsonValue;
}

export function enqueueResearch(
  state: ResearchState,
  kind: ResearchOrder["kind"],
  target: string,
): ResearchState & JsonValue {
  validateTarget(kind, target);
  if (state.queue.length >= LIMITS.researchQueue)
    throw new TypeError("Research queue is limited to 16 orders");
  const order = Object.freeze({
    id: `research-${state.nextOrderId}`,
    kind,
    target,
  }) as ResearchOrder;
  return clone({
    ...state,
    nextOrderId: state.nextOrderId + 1,
    queue: [...state.queue, order],
  }) as ResearchState & JsonValue;
}

export function cancelResearch(
  state: ResearchState,
  orderId: string,
): { readonly state: ResearchState & JsonValue; readonly refund: string } {
  if (state.active?.order.id === orderId)
    return {
      state: clone({ ...state, active: null }) as ResearchState & JsonValue,
      refund: formatMicro(((microFromString(state.active.reservedRp) * 75n) / 100n) as never),
    };
  if (!state.queue.some((order) => order.id === orderId))
    throw new TypeError(`Unknown research order: ${orderId}`);
  return {
    state: clone({
      ...state,
      queue: state.queue.filter((order) => order.id !== orderId),
    }) as ResearchState & JsonValue,
    refund: "0",
  };
}

export function repeatableCost(id: RepeatableId, level: number): bigint {
  if (!Number.isSafeInteger(level) || level < 0)
    throw new TypeError("Repeatable level must be nonnegative");
  const line = required(REPEATABLES, id);
  const growth = powerRational(rationalFromDecimal(line.costGrowth), BigInt(level));
  return ceilRational({
    numerator: BigInt(line.baseRpCost) * growth.numerator,
    denominator: growth.denominator,
  });
}

export function researchCost(state: ResearchState, order: ResearchOrder): number | bigint {
  return order.kind === "technology"
    ? required(TECHNOLOGIES, order.target).rpCost
    : order.kind === "specialization"
      ? required(SPECIALIZATIONS, order.target).rpCost
      : repeatableCost(order.target, state.repeatableLevels[order.target]);
}

export function researchDurationMs(order: ResearchOrder): number {
  const seconds =
    order.kind === "technology"
      ? required(TECHNOLOGIES, order.target).durationSeconds
      : order.kind === "specialization"
        ? required(SPECIALIZATIONS, order.target).durationSeconds
        : required(REPEATABLES, order.target).durationSeconds;
  return seconds * 1_000;
}

export function researchLegal(state: ResearchState, order: ResearchOrder): boolean {
  if (order.kind === "technology") {
    const tech = required(TECHNOLOGIES, order.target);
    return (
      !state.completed.includes(order.target) &&
      (!tech.prerequisite || state.completed.includes(tech.prerequisite as never))
    );
  }
  if (order.kind === "specialization") {
    const spec = required(SPECIALIZATIONS, order.target);
    return (
      spec.prerequisites.every((id) => state.completed.includes(id as never)) &&
      (spec.branch === "industrial"
        ? !state.industrialSpecialization
        : !state.militarySpecialization)
    );
  }
  return state.completed.includes("factory-works");
}

export function completeResearch(state: ResearchState): ResearchState & JsonValue {
  const active = state.active;
  if (!active) return state as ResearchState & JsonValue;
  const order = active.order;
  if (order.kind === "technology")
    return clone({
      ...state,
      active: null,
      completed: [...state.completed, order.target],
    }) as ResearchState & JsonValue;
  if (order.kind === "specialization")
    return clone({
      ...state,
      active: null,
      [required(SPECIALIZATIONS, order.target).branch === "industrial"
        ? "industrialSpecialization"
        : "militarySpecialization"]: order.target,
    }) as ResearchState & JsonValue;
  return clone({
    ...state,
    active: null,
    repeatableLevels: {
      ...state.repeatableLevels,
      [order.target]: state.repeatableLevels[order.target] + 1,
    },
  }) as ResearchState & JsonValue;
}

export function endResearch(state: ResearchState): ResearchState & JsonValue {
  if (!state.active && state.queue.length === 0) return state as ResearchState & JsonValue;
  return clone({ ...state, active: null, queue: [] }) as ResearchState & JsonValue;
}

export function startResearch(
  state: ResearchState,
  reservedRp: string,
  completesAtMs: number,
): ResearchState & JsonValue {
  const order = state.queue[0];
  if (!order) return state as ResearchState & JsonValue;
  return clone({
    ...state,
    active: { order, reservedRp, completesAtMs },
    queue: state.queue.slice(1),
  }) as ResearchState & JsonValue;
}

export function validateResearchState(value: unknown): ResearchState & JsonValue {
  const source = recordObject(value, "research state");
  exactKeys(source, [
    "nextOrderId",
    "queue",
    "active",
    "completed",
    "industrialSpecialization",
    "militarySpecialization",
    "repeatableLevels",
  ]);
  if (!Number.isSafeInteger(source.nextOrderId) || (source.nextOrderId as number) < 1)
    throw new TypeError("Invalid next research ID");
  if (!Array.isArray(source.queue) || source.queue.length > LIMITS.researchQueue)
    throw new TypeError("Invalid research queue");
  const queue = source.queue.map(validateOrder);
  const active = source.active === null ? null : validateActive(source.active);
  if (
    !Array.isArray(source.completed) ||
    !source.completed.every((id) => TECHNOLOGIES.some((tech) => tech.id === id))
  )
    throw new TypeError("Invalid completed research");
  const completed = source.completed as ResearchState["completed"];
  if (new Set(completed).size !== completed.length)
    throw new TypeError("Completed research must be unique");
  const industrialSpecialization = validateSpecialization(
    source.industrialSpecialization,
    "industrial",
  );
  const militarySpecialization = validateSpecialization(source.militarySpecialization, "military");
  const repeatableLevels = validateLevels(source.repeatableLevels);
  const ids = [...queue, ...(active ? [active.order] : [])].map((order) => order.id);
  if (new Set(ids).size !== ids.length) throw new TypeError("Research order IDs must be unique");
  const greatest = ids.reduce((value, id) => Math.max(value, Number(id.slice(9))), 0);
  if ((source.nextOrderId as number) <= greatest)
    throw new TypeError("Next research ID must exceed queued IDs");
  const state: ResearchState = {
    nextOrderId: source.nextOrderId as number,
    queue,
    active,
    completed,
    industrialSpecialization,
    militarySpecialization,
    repeatableLevels,
  };
  return clone(state) as ResearchState & JsonValue;
}

function validateOrder(value: unknown): ResearchOrder {
  const source = recordObject(value, "research order");
  exactKeys(source, ["id", "kind", "target"]);
  if (typeof source.id !== "string" || !/^research-[1-9][0-9]*$/.test(source.id))
    throw new TypeError("Invalid research order ID");
  if (
    source.kind !== "technology" &&
    source.kind !== "specialization" &&
    source.kind !== "repeatable"
  )
    throw new TypeError("Invalid research kind");
  if (typeof source.target !== "string") throw new TypeError("Invalid research target");
  validateTarget(source.kind, source.target);
  return Object.freeze({
    id: source.id,
    kind: source.kind,
    target: source.target,
  }) as ResearchOrder;
}

function validateActive(value: unknown): NonNullable<ResearchState["active"]> {
  const source = recordObject(value, "active research");
  exactKeys(source, ["order", "reservedRp", "completesAtMs"]);
  const reserved = typeof source.reservedRp === "string" ? microFromString(source.reservedRp) : 0n;
  if (
    reserved <= 0n ||
    !Number.isSafeInteger(source.completesAtMs) ||
    (source.completesAtMs as number) < 0
  )
    throw new TypeError("Invalid active research");
  return Object.freeze({
    order: validateOrder(source.order),
    reservedRp: formatMicro(reserved as never),
    completesAtMs: source.completesAtMs as number,
  });
}

function validateSpecialization(value: unknown, branch: "industrial" | "military") {
  if (value === null) return null;
  const specialization = SPECIALIZATIONS.find(
    (entry) => entry.id === value && entry.branch === branch,
  );
  if (!specialization) throw new TypeError(`Invalid ${branch} specialization`);
  return specialization.id;
}

function validateLevels(value: unknown): ResearchState["repeatableLevels"] {
  const source = recordObject(value, "repeatable levels");
  exactKeys(
    source,
    REPEATABLES.map((line) => line.id),
  );
  if (Object.values(source).some((level) => !Number.isSafeInteger(level) || (level as number) < 0))
    throw new TypeError("Invalid repeatable level");
  return Object.freeze({ ...source }) as ResearchState["repeatableLevels"];
}

function validateTarget(kind: ResearchOrder["kind"], target: string): void {
  const values =
    kind === "technology"
      ? TECHNOLOGIES
      : kind === "specialization"
        ? SPECIALIZATIONS
        : REPEATABLES;
  if (!values.some((entry) => entry.id === target))
    throw new TypeError(`Invalid ${kind}: ${target}`);
}
function required<T extends { readonly id: string }>(values: readonly T[], id: string): T {
  const value = values.find((entry) => entry.id === id);
  if (!value) throw new TypeError(`Unknown research: ${id}`);
  return value;
}
function clone(state: ResearchState): ResearchState {
  return Object.freeze({
    ...state,
    queue: Object.freeze(state.queue.map((order) => Object.freeze({ ...order }))),
    active: state.active
      ? Object.freeze({ ...state.active, order: Object.freeze({ ...state.active.order }) })
      : null,
    completed: Object.freeze([...state.completed]),
    repeatableLevels: Object.freeze({ ...state.repeatableLevels }),
  });
}
